import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import SearchBar from "@/components/SearchBar";
import FilterSidebar from "@/components/FilterSidebar";
import UseCaseGrid from "@/components/UseCaseGrid";
import UseCaseModal from "@/components/UseCaseModal";
import AddUseCaseModal from "@/components/AddUseCaseModal";
import EditUseCaseModal from "@/components/EditUseCaseModal";
import DeleteConfirmModal from "@/components/DeleteConfirmModal";
import AdminSettingsModal from "@/components/AdminSettingsModal";
import AuditLogModal from "@/components/AuditLogModal";
import ActiveFilters from "@/components/ActiveFilters";
import EmptyState from "@/components/EmptyState";

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [useCases, setUseCases] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    industries: [],
    products: [],
    useCases: [],
    markets: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUseCase, setEditingUseCase] = useState(null);
  const [deletingUseCase, setDeletingUseCase] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Get filters from URL
  const searchQuery = searchParams.get("q") || "";
  const selectedProducts = searchParams.get("products")?.split(",").filter(Boolean) || [];
  const selectedIndustries = searchParams.get("industries")?.split(",").filter(Boolean) || [];
  const selectedUseCaseTypes = searchParams.get("useCases")?.split(",").filter(Boolean) || [];
  const selectedMarkets = searchParams.get("markets")?.split(",").filter(Boolean) || [];
  const sortBy = searchParams.get("sort") || "newest";

  // Fetch use cases
  const fetchUseCases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/usecases`);
      setUseCases(response.data);
    } catch (error) {
      console.error("Error fetching use cases:", error);
      toast.error("Failed to load use cases");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/filter-options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, []);

  // Seed initial data
  const seedData = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/seed`);
      if (response.data.seeded) {
        toast.success("Use cases loaded successfully!");
        fetchUseCases();
        fetchFilterOptions();
      }
    } catch (error) {
      console.error("Error seeding data:", error);
    }
  }, [fetchUseCases, fetchFilterOptions]);

  useEffect(() => {
    const initData = async () => {
      await seedData();
      await fetchUseCases();
      await fetchFilterOptions();
    };
    initData();
  }, []);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "/" && !e.ctrlKey && !e.metaKey) {
        const activeElement = document.activeElement;
        if (activeElement.tagName !== "INPUT" && activeElement.tagName !== "TEXTAREA") {
          e.preventDefault();
          document.getElementById("search-input")?.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Update URL params
  const updateFilters = useCallback((key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value.length > 0) {
      newParams.set(key, Array.isArray(value) ? value.join(",") : value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Filter and sort use cases
  const filteredUseCases = useMemo(() => {
    let filtered = [...useCases];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(uc => 
        uc.title.toLowerCase().includes(query) ||
        uc.summary.toLowerCase().includes(query) ||
        uc.solution.toLowerCase().includes(query) ||
        uc.tags.some(tag => tag.toLowerCase().includes(query)) ||
        uc.painPoints.some(pp => pp.toLowerCase().includes(query)) ||
        uc.industry.some(i => i.toLowerCase().includes(query)) ||
        uc.product.some(p => p.toLowerCase().includes(query)) ||
        uc.useCase.some(uc => uc.toLowerCase().includes(query)) ||
        uc.market.some(m => m.toLowerCase().includes(query))
      );
    }

    // Product filter
    if (selectedProducts.length > 0) {
      filtered = filtered.filter(uc => 
        selectedProducts.some(p => uc.product.includes(p))
      );
    }

    // Industry filter
    if (selectedIndustries.length > 0) {
      filtered = filtered.filter(uc => 
        selectedIndustries.some(i => uc.industry.includes(i))
      );
    }

    // Use case type filter
    if (selectedUseCaseTypes.length > 0) {
      filtered = filtered.filter(uc => 
        selectedUseCaseTypes.some(t => uc.useCase.includes(t))
      );
    }

    // Market filter
    if (selectedMarkets.length > 0) {
      filtered = filtered.filter(uc => 
        selectedMarkets.some(m => uc.market.includes(m))
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "alphabetical":
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "industry":
        filtered.sort((a, b) => (a.industry[0] || "").localeCompare(b.industry[0] || ""));
        break;
      case "product":
        filtered.sort((a, b) => (a.product[0] || "").localeCompare(b.product[0] || ""));
        break;
      default:
        break;
    }

    return filtered;
  }, [useCases, searchQuery, selectedProducts, selectedIndustries, selectedUseCaseTypes, selectedMarkets, sortBy]);

  // Analytics logging
  const logAnalytics = useCallback((action, data) => {
    console.log(`[Analytics] ${new Date().toISOString()} - ${action}:`, data);
  }, []);

  const handleViewUseCase = useCallback((useCase) => {
    logAnalytics("VIEW_USE_CASE", { id: useCase.id, title: useCase.title });
    setSelectedUseCase(useCase);
  }, [logAnalytics]);

  const handleDownloadUseCase = useCallback((useCase) => {
    logAnalytics("DOWNLOAD_USE_CASE", { id: useCase.id, title: useCase.title });
  }, [logAnalytics]);

  const handleAddUseCase = useCallback(async (newUseCase) => {
    try {
      const response = await axios.post(`${API}/usecases`, newUseCase);
      setUseCases(prev => [response.data, ...prev]);
      fetchFilterOptions();
      toast.success("Use case added successfully!");
      setShowAddModal(false);
      logAnalytics("ADD_USE_CASE", { id: response.data.id, title: response.data.title });
    } catch (error) {
      console.error("Error adding use case:", error);
      toast.error("Failed to add use case");
    }
  }, [fetchFilterOptions, logAnalytics]);

  const handleEditUseCase = useCallback(async (id, updatedData) => {
    try {
      const response = await axios.put(`${API}/usecases/${id}`, updatedData);
      setUseCases(prev => prev.map(uc => uc.id === id ? response.data : uc));
      fetchFilterOptions();
      toast.success("Use case updated successfully!");
      setEditingUseCase(null);
      setSelectedUseCase(null);
      logAnalytics("EDIT_USE_CASE", { id, title: updatedData.title });
    } catch (error) {
      console.error("Error updating use case:", error);
      toast.error("Failed to update use case");
    }
  }, [fetchFilterOptions, logAnalytics]);

  const handleDeleteUseCase = useCallback(async (id) => {
    try {
      setIsDeleting(true);
      await axios.delete(`${API}/usecases/${id}`);
      setUseCases(prev => prev.filter(uc => uc.id !== id));
      fetchFilterOptions();
      toast.success("Use case deleted successfully!");
      setDeletingUseCase(null);
      setSelectedUseCase(null);
      logAnalytics("DELETE_USE_CASE", { id });
    } catch (error) {
      console.error("Error deleting use case:", error);
      toast.error("Failed to delete use case");
    } finally {
      setIsDeleting(false);
    }
  }, [fetchFilterOptions, logAnalytics]);

  const handleOpenEdit = useCallback((useCase) => {
    setSelectedUseCase(null);
    setEditingUseCase(useCase);
  }, []);

  const handleOpenDelete = useCallback((useCase) => {
    setSelectedUseCase(null);
    setDeletingUseCase(useCase);
  }, []);

  const hasActiveFilters = searchQuery || selectedProducts.length > 0 || selectedIndustries.length > 0 || selectedUseCaseTypes.length > 0 || selectedMarkets.length > 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard">
      <Header 
        totalCount={useCases.length} 
        filteredCount={filteredUseCases.length}
        onAddClick={() => setShowAddModal(true)}
        onSettingsClick={() => setShowSettings(true)}
      />
      
      <div className="px-4 md:px-8 lg:px-12 py-6">
        <SearchBar 
          value={searchQuery}
          onChange={(value) => updateFilters("q", value)}
          sortBy={sortBy}
          onSortChange={(value) => updateFilters("sort", value)}
        />
        
        <ActiveFilters
          selectedProducts={selectedProducts}
          selectedIndustries={selectedIndustries}
          selectedUseCaseTypes={selectedUseCaseTypes}
          selectedMarkets={selectedMarkets}
          onRemoveProduct={(p) => updateFilters("products", selectedProducts.filter(x => x !== p))}
          onRemoveIndustry={(i) => updateFilters("industries", selectedIndustries.filter(x => x !== i))}
          onRemoveUseCaseType={(t) => updateFilters("useCases", selectedUseCaseTypes.filter(x => x !== t))}
          onRemoveMarket={(m) => updateFilters("markets", selectedMarkets.filter(x => x !== m))}
          onClearAll={clearAllFilters}
        />

        <div className="flex gap-6 mt-6">
          <FilterSidebar
            open={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            filterOptions={filterOptions}
            selectedProducts={selectedProducts}
            selectedIndustries={selectedIndustries}
            selectedUseCaseTypes={selectedUseCaseTypes}
            selectedMarkets={selectedMarkets}
            onProductChange={(products) => updateFilters("products", products)}
            onIndustryChange={(industries) => updateFilters("industries", industries)}
            onUseCaseTypeChange={(types) => updateFilters("useCases", types)}
            onMarketChange={(markets) => updateFilters("markets", markets)}
            useCases={useCases}
          />

          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : filteredUseCases.length === 0 ? (
              <EmptyState 
                hasFilters={hasActiveFilters}
                onClearFilters={clearAllFilters}
              />
            ) : (
              <UseCaseGrid 
                useCases={filteredUseCases}
                searchQuery={searchQuery}
                onView={handleViewUseCase}
                onDownload={handleDownloadUseCase}
              />
            )}
          </main>
        </div>
      </div>

      {selectedUseCase && (
        <UseCaseModal
          useCase={selectedUseCase}
          onClose={() => setSelectedUseCase(null)}
          onDownload={handleDownloadUseCase}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
        />
      )}

      {showAddModal && (
        <AddUseCaseModal
          filterOptions={filterOptions}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUseCase}
        />
      )}

      {editingUseCase && (
        <EditUseCaseModal
          useCase={editingUseCase}
          filterOptions={filterOptions}
          onClose={() => setEditingUseCase(null)}
          onSubmit={handleEditUseCase}
        />
      )}

      {deletingUseCase && (
        <DeleteConfirmModal
          useCase={deletingUseCase}
          onClose={() => setDeletingUseCase(null)}
          onConfirm={handleDeleteUseCase}
          isDeleting={isDeleting}
        />
      )}

      {showSettings && (
        <AdminSettingsModal
          onClose={() => setShowSettings(false)}
          onDataChanged={() => {
            fetchUseCases();
            fetchFilterOptions();
          }}
          onOpenAuditLog={() => setShowAuditLog(true)}
        />
      )}

      {showAuditLog && (
        <AuditLogModal onClose={() => setShowAuditLog(false)} />
      )}
    </div>
  );
};

export default Dashboard;
